// File: webelieve-frontend/src/features/contacts/hooks/useClientScorecards.ts
// Hook for fetching all Predictive Intake Scorecards for a client's matters.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { getApiUrl } from '../../../config/api.config';

// ─── Provenance Types ─────────────────────────────────────────

export interface SourceRef {
  type: 'matter_field' | 'document_metadata' | 'document_content' | 'party_relationship' | 'intake_submission' | 'llm_extraction' | 'public_prior' | 'practice_area_config' | 'computed' | 'bayesian_update';
  entity_id?: string;
  entity_label?: string;
  field?: string;
  excerpt?: string;
}

export interface ScorecardFactor {
  factor: string;
  impact: 'positive' | 'negative' | 'neutral';
  points: number;
  source_ref?: SourceRef;
}

// ─── Types ────────────────────────────────────────────────────

export interface MatterScorecard {
  scorecard_id: string;
  matter_id: string;
  client_id: string;
  schema_version: string;

  // Matter relation (loaded via backend join)
  matter?: {
    matter_id: string;
    title: string;
    displayNumber: string;
    practice_area: string;
    status: string;
  };

  // Primary metrics
  expected_recovery_low: number | null;
  expected_recovery_high: number | null;
  expected_recovery_median: number | null;
  profitability_probability: number | null;
  timeline_months_low: number | null;
  timeline_months_high: number | null;
  firm_profit_projection: number | null;
  firm_roi_multiple: number | null;
  risk_level: string;
  overall_composite_score: number | null;

  // Supporting metrics (JSONB) — factors now include source_ref for provenance
  data_completeness: {
    score: number;
    filled_count: number;
    total_count: number;
    missing_critical: string[];
    missing_important: string[];
    missing_standard: string[];
    recommendations: { action: string; impact_points: number; impact_narrative: string; priority: string; field_key: string; source_ref?: SourceRef }[];
  } | null;
  liability_strength: { score: number; factors: ScorecardFactor[]; base_rate: number; evidence_count: number } | null;
  damages_injury_profile: { score: number; documented_specials: number; injury_types: string[]; factors: ScorecardFactor[] } | null;
  opposing_party_insights: { score: number; factors: ScorecardFactor[]; insurance_carrier: string; defendant_type: string } | null;
  venue_judge_benchmarks: { score: number; factors?: ScorecardFactor[]; jurisdiction: string; plaintiff_win_rate: number; judge_assigned: boolean } | null;
  client_plaintiff_profile: { score: number; factors: ScorecardFactor[]; cooperation_rating: string; prior_claims: number | null } | null;
  red_flags_green_lights: {
    score: number;
    red_flags: { flag: string; severity: string; points: number; source_ref?: SourceRef }[];
    green_lights: { flag: string; strength: string; points: number; source_ref?: SourceRef }[];
    net_assessment: string;
  } | null;

  // LLM-extracted data (JSONB columns — used by source table in AIInsightsPanel)
  public_prior_data: Record<string, any> | null;
  case_evidence_data: Record<string, any> | null;

  // Audit
  confidence_level: string;
  similar_case_count: number;
  bayesian_update_count: number;
  triggered_by: string | null;
  computed_at: string;
  computation_duration_ms: number | null;
}

export interface ClientScorecardSummary {
  totalMatters: number;
  averageCompositeScore: number;
  totalProjectedRecovery: { low: number; high: number };
  totalFirmProfit: number;
  riskDistribution: Record<string, number>;
  highestRiskMatter: MatterScorecard | null;
  bestPerformingMatter: MatterScorecard | null;
}

// ─── Hook ─────────────────────────────────────────────────────

export const useClientScorecards = (clientId: string) => {
  const { keycloak } = useKeycloak();

  const [scorecards, setScorecards] = useState<MatterScorecard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${keycloak?.token}`,
  }), [keycloak?.token]);

  const fetchScorecards = useCallback(async () => {
    if (!clientId || !keycloak?.token) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(getApiUrl(`/api/v1/scorecard/clients/${clientId}`), {
        headers: getHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          setScorecards([]);
          return;
        }
        throw new Error(`Failed to fetch client scorecards: ${response.statusText}`);
      }

      const data = await response.json();
      setScorecards(data.scorecards ?? []);
    } catch (err: any) {
      console.error('[useClientScorecards] error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, keycloak?.token, getHeaders]);

  const refreshMatterScorecard = useCallback(async (matterId: string) => {
    if (!keycloak?.token) return;

    try {
      const response = await fetch(getApiUrl(`/api/v1/scorecard/matters/${matterId}/refresh`), {
        method: 'POST',
        headers: getHeaders(),
      });

      if (response.ok) {
        // Re-fetch all scorecards after single matter refresh
        await fetchScorecards();
      }
    } catch (err: any) {
      console.error('[useClientScorecards] refresh error:', err);
    }
  }, [keycloak?.token, getHeaders, fetchScorecards]);

  // Explicitly trigger LLM-powered computation for all client matters (re-computes even if existing)
  const computeAllScorecards = useCallback(async () => {
    if (!clientId || !keycloak?.token) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(getApiUrl(`/api/v1/scorecard/clients/${clientId}/compute-all`), {
        method: 'POST',
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to compute scorecards: ${response.statusText}`);
      }

      const data = await response.json();
      setScorecards(data.scorecards ?? []);
    } catch (err: any) {
      console.error('[useClientScorecards] computeAll error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, keycloak?.token, getHeaders]);

  // Compute client-level summary from individual matter scorecards
  const summary: ClientScorecardSummary = useMemo(() => {
    if (!scorecards.length) {
      return {
        totalMatters: 0,
        averageCompositeScore: 0,
        totalProjectedRecovery: { low: 0, high: 0 },
        totalFirmProfit: 0,
        riskDistribution: {},
        highestRiskMatter: null,
        bestPerformingMatter: null,
      };
    }

    const withScores = scorecards.filter(s => s.overall_composite_score != null);
    const avgScore = withScores.length > 0
      ? withScores.reduce((sum, s) => sum + Number(s.overall_composite_score), 0) / withScores.length
      : 0;

    const recoveryLow = scorecards.reduce((sum, s) => sum + Number(s.expected_recovery_low ?? 0), 0);
    const recoveryHigh = scorecards.reduce((sum, s) => sum + Number(s.expected_recovery_high ?? 0), 0);
    const totalProfit = scorecards.reduce((sum, s) => sum + Number(s.firm_profit_projection ?? 0), 0);

    const riskDist: Record<string, number> = {};
    scorecards.forEach(s => {
      const level = s.risk_level || 'unknown';
      riskDist[level] = (riskDist[level] || 0) + 1;
    });

    const riskOrder = ['very_high', 'high', 'elevated', 'moderate', 'low', 'minimal', 'very_low'];
    const highestRisk = [...scorecards].sort(
      (a, b) => riskOrder.indexOf(a.risk_level) - riskOrder.indexOf(b.risk_level)
    )[0] || null;

    const bestPerforming = [...withScores].sort(
      (a, b) => Number(b.overall_composite_score) - Number(a.overall_composite_score)
    )[0] || null;

    return {
      totalMatters: scorecards.length,
      averageCompositeScore: Math.round(avgScore),
      totalProjectedRecovery: { low: recoveryLow, high: recoveryHigh },
      totalFirmProfit: totalProfit,
      riskDistribution: riskDist,
      highestRiskMatter: highestRisk,
      bestPerformingMatter: bestPerforming,
    };
  }, [scorecards]);

  // Initial load
  useEffect(() => {
    if (clientId) fetchScorecards();
  }, [clientId, fetchScorecards]);

  // Socket.IO real-time listener for scorecard updates
  useEffect(() => {
    const socket = (window as any).__edgelexSocket;
    if (!socket) return;

    const handler = () => fetchScorecards();
    socket.on('scorecard:updated', handler);
    return () => { socket.off('scorecard:updated', handler); };
  }, [fetchScorecards]);

  return {
    scorecards,
    summary,
    loading,
    error,
    fetchScorecards,
    refreshMatterScorecard,
    computeAllScorecards,
  };
};
